import { useEffect, useState } from 'react';
import { Outlet, Navigate, Link, useLocation } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '../state/AuthContext';
import { PageHeader } from '../shell/PageHeader';
import { ShellPage } from '../shell/ShellPage';
import { useShellConfiguration } from '../shell/ShellLayoutContext';
import { settingsWorkspaceShell } from '../shell/contextualNav';
import { SpoxLoader } from '../shell/SpoxLoader';

/**
 * Settings shell (Stage 19I). Integrations owns a wider canvas layout and its
 * own "Settings › Integrations" header (persistent left nav + tabs + rail), so
 * this layout skips the generic header/list-width constraint for that child
 * rather than doubling up on chrome.
 */
export function SettingsLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isIntegrations = location.pathname.startsWith('/settings/integrations');
  useShellConfiguration(settingsWorkspaceShell());

  if (loading) {
    return <SpoxLoader label="Loading Spox settings" testId="settings-bootstrap-loader" />;
  }

  if (!user) {
    return <Navigate to="/login?next=/settings" replace />;
  }

  return (
    <ShellPage
      mode={isIntegrations ? 'canvas' : 'list'}
      className={`settings-page${isIntegrations ? ' settings-page--integrations' : ''}`}
      testId="settings-layout"
    >
      {!isIntegrations && (
        <PageHeader title="Settings" subtitle="Account, organisation, and security." />
      )}
      <Outlet />
    </ShellPage>
  );
}

export { SettingsProfileScreen } from './SettingsProfileScreen';
export { SettingsAccountScreen } from './SettingsAccountScreen';
export { SettingsOrganisationScreen } from './SettingsOrganisationScreen';
export { SettingsPublicationsScreen } from './SettingsPublicationsScreen';
export { SettingsSecurityScreen } from './SettingsSecurityScreen';

export function SettingsTeamScreen() {
  const { organisations } = useAuth();
  const org = organisations[0];
  const [items, setItems] = useState<
    Array<{ userId: string; fullName: string; email: string; role: string }>
  >([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!org?.id) return;
    void api
      .authOrganisationTeam(org.id)
      .then((res) => setItems(res.items))
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [org?.id]);

  return (
    <section className="panel stack" data-testid="settings-team">
      <h2>Team & Roles</h2>
      <p className="muted">
        Members of {org?.name ?? 'your organisation'}. Roles: Owner, Admin, Editor, Contributor,
        Viewer.
      </p>
      {error ? <p className="error">{error}</p> : null}
      {!org?.id ? (
        <p className="muted">No organisation yet.</p>
      ) : (
        <ul className="lifecycle-history">
          {items.map((m) => (
            <li key={m.userId}>
              <strong>{m.fullName}</strong>
              <span className="muted">
                {' '}
                · {m.email} · {m.role}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="muted">Invites and role edits expand in a later stage.</p>
    </section>
  );
}

export function SettingsPreferencesScreen() {
  return (
    <section className="panel stack" data-testid="settings-preferences">
      <h2>Preferences</h2>
      <p className="muted">
        Locale and notification preferences will land in a later stage. Profile timezone is edited
        under Profile.
      </p>
      <Link className="btn secondary" to="/settings/profile">
        Edit profile timezone
      </Link>
    </section>
  );
}
